import { useEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Toast } from '@plone/components';
import { createContent } from '@plone/volto/actions/content/content';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { usePrevious } from '@plone/volto/helpers/Utils/usePrevious';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import cx from 'classnames';
import {
  Button,
  Checkbox,
  Container,
  Form,
  Grid,
  Label,
  Segment,
} from 'semantic-ui-react';
import { SelectField } from '../Fields/SelectField';
import { TextField } from '../Fields/TextField';
import { areaGovernVocabulary } from './vocabularies/area_govern_vocabulary';
import { emdVocabulary } from './vocabularies/emd_vocabulary';
import { llistaVocabulary } from './vocabularies/llista_vocabulary';
import { townsVocabulary } from './vocabularies/towns_vocabulary';

const messages = defineMessages({
  title: {
    id: 'Registration form',
    defaultMessage: 'Registration form',
  },
  default: {
    id: 'Default',
    defaultMessage: 'Default',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  nameTitle: {
    id: 'name-title',
    defaultMessage: 'Name',
  },
  surnameTitle: {
    id: 'surname-title',
    defaultMessage: 'First surname',
  },
  surname2Title: {
    id: 'surname2-title',
    defaultMessage: 'Second surname',
  },
  dniTitle: {
    id: 'dni',
    defaultMessage: 'DNI',
  },
  dniPlaceholder: {
    id: 'dni-placeholder',
    defaultMessage: '12345678A',
  },
  emailTitle: {
    id: 'email-title',
    defaultMessage: 'E-mail',
  },
  phoneTitle: {
    id: 'phone-title',
    defaultMessage: 'Phone',
  },
  townTitle: {
    id: 'town-title',
    defaultMessage: 'Town',
  },
  emdTitle: {
    id: 'emd-title',
    defaultMessage:
      'EMD where you were on the list in the 2023 municipal elections',
  },
  llistaTitle: {
    id: 'llista-title',
    defaultMessage: 'What position did you occupy on the list?',
  },
  areaGovenmentTitle: {
    id: 'area-govenment-title',
    defaultMessage:
      'If you are in government, what is your main area of governance? (classified according to Junts sectoral areas)',
  },
  areaGovenmentTitle2: {
    id: 'area-govenment-title-2',
    defaultMessage:
      'If you manage another different area of governance, which area does it belong to? (classified according to Junts sectoral areas)',
  },
  successRegisterCompletedTitle: {
    id: 'Account Registration Completed',
    defaultMessage: 'Account Registration Completed',
  },
  successRegisterCompletedBody: {
    id: 'The registration process has been successful. Please check your e-mail inbox for information on how to activate your account.',
    defaultMessage:
      'The registration process has been successful. Please check your e-mail inbox for information on how to activate your account.',
  },
  registerFailed: {
    id: 'Register Failed',
    defaultMessage: 'Register Failed',
  },
  registerFailedContent: {
    id: 'The registration process has failed. Please try again.',
    defaultMessage: 'The registration process has failed. Please try again.',
  },
  registerFailedRequiredFields: {
    id: 'Required fields',
    defaultMessage: 'Required fields',
  },
  registerFailedRequiredFieldsContent: {
    id: 'Please fill in all required fields.',
    defaultMessage: 'Please fill in all required fields.',
  },
  register: {
    id: 'Register',
    defaultMessage: 'Register',
  },
  dniInvalidFormat: {
    id: 'DNI invalid format',
    defaultMessage: 'DNI invalid format',
  },
  dniAlreadyExists: {
    id: 'DNI already exists',
    defaultMessage:
      'Some of the data entered is already registered, please contact politicamunicipal@junts.cat. \n\n If you think you may already be registered, access with your NIF account registered in the Junts Decidim Space.',
  },
  termsAndConditions: {
    id: 'terms-and-conditions-title',
    defaultMessage: "I've read and accept the terms and conditions",
  },
  commitToObeyJuntsCode: {
    id: 'commit-to-obey-junts-code-title',
    defaultMessage:
      'I know, accept, and commit to fully comply with the Junts per Catalunya Code of Ethics that is in force at any given time.',
  },
  privacyPolicy: {
    id: 'privacy-policy-title',
    defaultMessage: 'Privacy Policy',
  },
  labelRequiredField: {
    id: 'label-required-field',
    defaultMessage: 'Required field',
  },
});
export interface VocabularyItem {
  value: string;
  title: string;
}
interface RegisterUserState {
  content: {
    subrequests: {
      registerUser: {
        error: {
          [key: string]: any;
        };
        loading: boolean;
        loaded: boolean;
      };
    };
  };
}
const useUsers = () => {
  const error = useSelector(
    (state: RegisterUserState) =>
      state.content.subrequests?.registerUser?.error,
  );
  const loading = useSelector(
    (state: RegisterUserState) =>
      state.content.subrequests?.registerUser?.loading,
  );
  const loaded = useSelector(
    (state: RegisterUserState) =>
      state.content.subrequests?.registerUser?.loaded,
  );
  return { error, loaded, loading };
};

const Register = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const { loaded, loading, error } = useUsers();
  const [errorList, setErrorList] = useState<string[]>([]);
  const token = useSelector(
    (state: any) => state.userSession.token,
    shallowEqual,
  );
  const previousToken = usePrevious(token);
  const prevloading = usePrevious(loading);

  useEffect(() => {
    if (token && token !== previousToken) {
      history.push('/');
    }
  }, [token, previousToken, history]);

  useEffect(() => {
    if (prevloading && loaded) {
      toast.success(
        <Toast
          success
          title={intl.formatMessage(messages.successRegisterCompletedTitle)}
          content={intl.formatMessage(messages.successRegisterCompletedBody)}
        />,
      );
      history.push('/');
    }
  }, [intl, history, loaded, prevloading]);

  useEffect(() => {
    if (error) {
      if (error.response?.body?.message) {
        try {
          const text = error.response?.body?.message;
          const fixedText = text.replace(/'/g, '"');
          if (fixedText.includes('it is already in use')) {
            toast.error(
              <Toast
                error
                title={intl.formatMessage(messages.registerFailed)}
                content={intl.formatMessage(messages.dniAlreadyExists)}
              />,
              { autoClose: false, toastId: 'registerFailedDniAlreadyExists' },
            );
            return;
          }

          const json = JSON.parse(fixedText);
          if (json[0].field === 'dni') {
            setErrorList((prev) => [...prev, 'dni']);
            toast.error(
              <Toast
                error
                title={intl.formatMessage(messages.registerFailed)}
                content={intl.formatMessage(messages.dniInvalidFormat)}
              />,
              { autoClose: false, toastId: 'registerFailedDniInvalidFormat' },
            );
            return;
          } else if (json[0].message) {
            toast.error(
              <Toast
                error
                title={intl.formatMessage(messages.registerFailed)}
                content={json[0].message}
              />,
              { autoClose: false, toastId: 'genericAPIError' },
            );
            return;
          }
        } catch (e) {
          console.log('error', e);
        }
      }

      if (!toast.isActive('registerFailed')) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messages.registerFailed)}
            content={intl.formatMessage(messages.registerFailedContent)}
          />,
          { autoClose: false, toastId: 'registerFailed' },
        );
      }
    }
  }, [error, intl]);

  const vocabularyTowns = useMemo(() => {
    return townsVocabulary
      .map((item) => ({
        value: item.token,
        label: item.title,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const vocabularyEMD = useMemo(() => {
    return emdVocabulary
      .map((item) => ({
        value: item.token,
        label: item.title,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const vocabularyAreaGovern = useMemo(() => {
    return areaGovernVocabulary
      .map((item) => ({
        value: item.token,
        label: item.title,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const vocabularyLlista = useMemo(() => {
    return llistaVocabulary
      .map((item) => ({
        value: item.token,
        label: item.title,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, []);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    toast.dismiss();
    setErrorList([]);
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const data = {
      name: formData.get('name') as string,
      surname_1: formData.get('surname_1') as string,
      surname_2: formData.get('surname_2') as string,
      dni: formData.get('dni') as string,
      email: formData.get('email') as string,
      phone: formData.get('phone') as string,
      town: formData.get('town') as string,
      EMD: formData.get('EMD') as string,
      llista: formData.get('llista') as string,
      termsAndConditions: formData.get('termsAndConditions') !== null,
      area_de_govern: formData.get('area_de_govern') as string,
      area_de_govern_2: formData.get('area_de_govern_2') as string,
      commitToObeyJuntsCode: formData.get('commitToObeyJuntsCode') !== null,
    };

    // Remove empty fields
    Object.keys(data).forEach((key) => {
      if (data[key] === '' || data[key] === null) {
        delete data[key];
      }
    });
    const newErrorList = [];
    if (!data.termsAndConditions) {
      newErrorList.push('termsAndConditions');
    }
    if (!data.commitToObeyJuntsCode) {
      newErrorList.push('commitToObeyJuntsCode');
    }
    if (!data.llista) {
      newErrorList.push('llista');
    }
    if (newErrorList.length > 0) {
      setErrorList(newErrorList);
      return;
    }

    dispatch(
      createContent(
        '/fitxes',
        {
          ...data,
          title: `${data.name} ${data.surname_1} ${data.surname_2 ?? ''}`,
          id: data.dni,
          '@type': 'fitxa',
        },
        'registerUser',
      ),
    );
  };

  return (
    <div id="page-register">
      <Helmet title={intl.formatMessage(messages.register)} />
      <Container>
        <h1>
          <strong>{intl.formatMessage(messages.register)}</strong>
        </h1>
      </Container>
      <Container>
        <Form method="post" onSubmit={onSubmit}>
          <Segment.Group raised>
            <Segment className="primary">
              {intl.formatMessage(messages.register)}
            </Segment>
            <Segment className="form">
              <TextField
                label={intl.formatMessage(messages.nameTitle)}
                field="name"
                required
              />
              <TextField
                label={intl.formatMessage(messages.surnameTitle)}
                field="surname_1"
                required
              />
              <TextField
                label={intl.formatMessage(messages.surname2Title)}
                field="surname_2"
              />
              <TextField
                label={intl.formatMessage(messages.dniTitle)}
                field="dni"
                hasError={errorList.includes('dni')}
                placeholder={intl.formatMessage(messages.dniPlaceholder)}
                required
              />
              <TextField
                label={intl.formatMessage(messages.emailTitle)}
                field="email"
                type="email"
                required
              />
              <TextField
                label={intl.formatMessage(messages.phoneTitle)}
                field="phone"
                required
              />

              <SelectField
                label={intl.formatMessage(messages.townTitle)}
                field="town"
                options={vocabularyTowns}
              />

              <SelectField
                label={intl.formatMessage(messages.emdTitle)}
                field="EMD"
                options={vocabularyEMD}
              />

              <SelectField
                label={intl.formatMessage(messages.llistaTitle)}
                field="llista"
                options={vocabularyLlista}
                required
                errorMessage={
                  errorList.includes('llista') &&
                  intl.formatMessage(messages.registerFailedRequiredFields)
                }
              />
              <SelectField
                label={intl.formatMessage(messages.areaGovenmentTitle)}
                field="area_de_govern"
                options={vocabularyAreaGovern}
              />
              <SelectField
                label={intl.formatMessage(messages.areaGovenmentTitle2)}
                field="area_de_govern_2"
                options={vocabularyAreaGovern}
              />
              <Form.Field inline className={cx('help', 'required')} columns={1}>
                <Grid>
                  <Grid.Row stretched>
                    <Grid.Column width="12">
                      <div className="wrapper">
                        <Checkbox
                          id="termsAndConditions"
                          name="termsAndConditions"
                          className="checkbox-wrapper"
                          label={
                            <label htmlFor="termsAndConditions">
                              {intl.formatMessage(messages.termsAndConditions)}
                              &nbsp;
                              <span className="label-required">
                                (
                                {intl.formatMessage(
                                  messages.labelRequiredField,
                                )}
                                )
                              </span>
                            </label>
                          }
                        />
                      </div>
                      {errorList.includes('termsAndConditions') && (
                        <Label basic color="red" className="form-error-label">
                          {intl.formatMessage(
                            messages.registerFailedRequiredFields,
                          )}
                        </Label>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form.Field>
              <Form.Field inline className={cx('help', 'required')} columns={1}>
                <Grid>
                  <Grid.Row stretched>
                    <Grid.Column width="12">
                      <div className="wrapper">
                        <Checkbox
                          id="commitToObeyJuntsCode"
                          name="commitToObeyJuntsCode"
                          className="checkbox-wrapper"
                          label={
                            <label htmlFor="commitToObeyJuntsCode">
                              {intl.formatMessage(
                                messages.commitToObeyJuntsCode,
                              )}
                              &nbsp;
                              <span className="label-required">
                                (
                                {intl.formatMessage(
                                  messages.labelRequiredField,
                                )}
                                )
                              </span>
                            </label>
                          }
                        />
                      </div>
                      {errorList.includes('commitToObeyJuntsCode') && (
                        <Label basic color="red" className="form-error-label">
                          {intl.formatMessage(
                            messages.registerFailedRequiredFields,
                          )}
                        </Label>
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Form.Field>
            </Segment>

            <Segment className="actions" clearing>
              <Button
                basic
                primary
                icon
                floated="right"
                type="submit"
                id="register-form-submit"
                aria-label={intl.formatMessage(messages.register)}
                title={intl.formatMessage(messages.register)}
                loading={loading}
              >
                <Icon className="circled" name={aheadSVG} size="30px" />
              </Button>
            </Segment>
            <Segment className="actions" clearing>
              <p>
                <strong>{intl.formatMessage(messages.privacyPolicy)}</strong>
              </p>
              <p className="legal-text">
                <FormattedMessage
                  id="legal-text"
                  defaultMessage="<cta>Responsable del tractament:</cta> JUNTS PER CATALUNYA. <cta>Finalitat del tractament:</cta> gestionar la teva participació en el programa de suport als electes. Això inclou la recepció de comunicacions, convocatòries a actes del partit, l’enregistrament audiovisual de la teva imatges i veu en totes les activitats en que participis com a electe i qualsevol altre tractament que es pugui donar com a conseqüència de les activitat que, com electe, has de portar a terme. Els enregistraments que es facin, que no tinguin el caràcter de privats, podran ser publicats a qualsevols mitjà de comunicació i xarxes socials si JUNTS PER CATALUNYA considera que és adient per la promoció de la seva activitat o en el desenvolupament de campanyes polítiques o electorals. <cta>Legitimació:</cta> l’interès legítim de JUNTS en totes les finalitats del tractament. <cta>Destinataris:</cta> les teves dades no seran cedides a tercers si no és per obligació legal o per donar compliment a la finalitat del tractament autoritzada. <cta>Drets:</cta> pots exercir els drets d’accés, rectificació, supressió i portabilitat de les teves dades a l’adreça <e>dpd@junts.cat</e>. <cta>Informació addicional:</cta> pots consultar informació més detallada sobre la nostra política de protecció de dades a l’apartat de Política de privacitat de la web de junts (<a>junts.cat</a>)."
                  values={{
                    a: (chunks) => (
                      <a
                        target="_blank"
                        href="https://junts.cat"
                        rel="noreferrer"
                      >
                        {chunks}
                      </a>
                    ),
                    e: (chunks) => (
                      <a
                        target="_blank"
                        href="mailto:dpd@junts.cat"
                        rel="noreferrer"
                      >
                        {chunks}
                      </a>
                    ),
                    cta: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </p>
            </Segment>
          </Segment.Group>
        </Form>
      </Container>
    </div>
  );
};

export default Register;
